import { Box, Typography } from '@mui/material';
import type { ContentMediaPosition } from '@provision/landing-page-utils';
import { useLayoutStyles } from '../layouts/layout-context';
import type { BlockRenderProps } from '../layouts/types';
import { hasBlockMedia } from '../media';
import { AccentText } from '../primitives/accent-text';
import { BlockMedia } from '../primitives/block-media';
import { SparkIcon } from '../primitives/icons';

// Text stays first in the DOM; `flip` swaps sides with CSS `order`, so mobile and screen readers read text first.
function gridVariant(
  mediaPosition: ContentMediaPosition,
  contentIndex: number,
  hasMedia: boolean,
): 'default' | 'flip' | 'single' {
  if (!hasMedia) return 'single';
  if (mediaPosition === 'auto') return contentIndex % 2 === 1 ? 'flip' : 'default';
  return mediaPosition === 'left' ? 'flip' : 'default';
}

export const ContentBlock = ({ block, contentIndex }: BlockRenderProps) => {
  const s = useLayoutStyles('content');
  if (block.type !== 'content') return null;

  const variant = gridVariant(block.mediaPosition, contentIndex, hasBlockMedia(block));

  return (
    <Box sx={s.section}>
      <Box sx={s.grid[variant]} data-variant={variant}>
        <div>
          {block.kicker ? (
            <Box sx={s.kicker}>
              <SparkIcon sx={s.kickerIcon} />
              <AccentText text={block.kicker} surface="light" />
            </Box>
          ) : null}
          <Typography component="h3" sx={s.title}>
            <AccentText text={block.title} surface="light" />
          </Typography>
          <Typography component="p" sx={s.body}>
            <AccentText text={block.body} surface="light" />
          </Typography>
        </div>
        <BlockMedia block={block} frameSx={s.media} />
      </Box>
    </Box>
  );
};
